import React, { useEffect, useState } from "react";
import { Col, ProgressBar, Row } from "react-bootstrap";
import ESPieChart from "../../controls/ESPieChart";
import ESPopUpForList from "../../controls/ESPopUpForList";
import { getPollResponse } from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import Loader from "../loader/Loader";
import liveico from "./../../assets/images/live.svg";
import "./PollResult.scss";

type PollResultProps = {
  setPollResultView: React.Dispatch<React.SetStateAction<boolean>>;
  surveysAssignmentId?: number;
  surveyFormId: number;
};

interface PollStatisticAssestmentInfo {
  Title: string;
  Count: number;
  Percentage: number;
  OptionValue: string;
  userdetail: User[];
}

interface User {
  UserId: number;
  fullName: string;
  Email: string;
  ContactNo: string;
  Username: string;
}

interface UserWithoutId {
  fullName: string;
  Email: string;
  ContactNo: string;
  Username: string;
}
interface SurveyResponse {
  Title: string;
  Count: number;
  Percentage: number;
  AverageRating: number;
  userdetail: User[];
}

interface UserFullNameOnly {
  fullName: string;
}

interface PollStatistics {
  TotalRespondents: number;
  TotalResponse: number;
  SurveyQuestionResponseDetails: SurveyResponse[];
  Uniqueusers: User[];
  PollStatisticAssestmentInfoList: PollStatisticAssestmentInfo[];
}

interface ApiResponseItem {
  QuestionResponseOptionId: number;
  OptionValue: string;
  Count: number;
  Percentage: number;
  TotalResponses: number;
  TotalFormResponses: number;
  SurveyQuestionResponseDetails: string;
  Uniqueusers: string;
}

const PollResult: React.FC<PollResultProps> = ({
  setPollResultView,
  surveysAssignmentId,
  surveyFormId,
}) => {
  const [loading, setLoading] = useState(false);
  const [pollStatistics, setPollStatistics] = useState<PollStatistics>();
  const [userPopUpData, setUserPopUpData] = useState<UserFullNameOnly[]>([]);
  const [popupHeader, setPopupHeader] = useState<string>("");
  const [showPopup, setShowPopup] = useState(false);

  const handleButtonClick = () => {
    setPollResultView(false);
  };

  useEffect(() => {
    fetchPollLiveResult();
    const interval = setInterval(() => {
      fetchPollLiveResult();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPollLiveResult = async () => {
    const payload = {
      SurveyAssignmentId: surveysAssignmentId,
      SurveyFormId: surveyFormId,
    };
    setLoading(true);
    try {
      const response: any = await axiosPost(getPollResponse, payload);
      if (response?.data?.Status !== 200) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
        const rawData = response?.data?.ExtraData;
        const transformedData: PollStatistics = {
          TotalRespondents: rawData[0]?.TotalFormResponses || 0,
          TotalResponse: rawData[0]?.TotalResponses || 0,
          PollStatisticAssestmentInfoList: rawData?.map((item: ApiResponseItem) => {
            let userList: User[] = [];
            try {
              const parsedDetails: SurveyResponse[] = JSON.parse(item?.SurveyQuestionResponseDetails || "[]");
              const matchedDetail = parsedDetails?.find((detail: SurveyResponse) => detail?.Title === item?.OptionValue);
              if (matchedDetail?.userdetail) {
                userList = typeof matchedDetail?.userdetail === 'string' 
                  ? JSON.parse(matchedDetail?.userdetail) 
                  : matchedDetail?.userdetail;
              }
            } catch (err) {
              console.error("Failed to parse userdetail", err);
            }

            return {
              Title: item?.OptionValue,
              Count: item?.Count,
              Percentage: item?.Percentage,
              OptionValue: item?.OptionValue,
              userdetail: userList,
            };
          }),
          SurveyQuestionResponseDetails: rawData[0]?.SurveyQuestionResponseDetails
            ? JSON.parse(rawData[0]?.SurveyQuestionResponseDetails)?.map(
                (response: SurveyResponse) => ({
                  ...response,
                  userdetail: typeof response?.userdetail === 'string'
                    ? JSON.parse(response?.userdetail)
                    : response?.userdetail || [],
                })
              )
            : [],
          Uniqueusers: rawData[0]?.Uniqueusers
            ? JSON.parse(rawData[0]?.Uniqueusers)
            : [],
        };
        setPollStatistics(transformedData);
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleCountClick = (users: User[], optionTitle: string) => {
    if (users?.length > 0) {
      const usersWithoutUserId: UserWithoutId[] = users
        ?.map(({ UserId, ...rest }) => rest)
        ?.filter((user) => user?.Email?.trim() && user?.Username?.trim());

      const uniqueUsers = usersWithoutUserId?.reduce((acc, current) => {
        const isDuplicate = acc?.some(
          (user) =>
            user?.Email === current?.Email &&
            user?.Username === current?.Username
        );
        if (!isDuplicate) {
          acc?.push(current);
        }
        return acc;
      }, [] as UserWithoutId[]);

      const fullNameOnlyData = uniqueUsers?.map(({ fullName }) => ({ fullName }));

      setUserPopUpData(fullNameOnlyData);
      const headerText = `Option-${optionTitle}`;
      setPopupHeader(headerText);
      setShowPopup(true);
    }
  };

  const handlepopupSHowHide = (newValue: boolean) => {
    setShowPopup(newValue);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="poll-result-wrapper">
          <Row>
            <Col>
              <div className="my-3">
                {pollStatistics?.PollStatisticAssestmentInfoList?.map(
                  (response: PollStatisticAssestmentInfo, index: number) => (
                    <div className="mb-3" key={index}>
                      <div className="d-flex justify-content-between">
                        <div>{response?.Title}</div>
                        <div>Count: {response?.Count}</div>
                        <button
                          style={{
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: 0,
                            font: "inherit",
                            color: "inherit",
                            textDecoration: "underline",
                            textDecorationColor: "#ccc",
                          }}
                          onClick={() => handleCountClick(response?.userdetail, response?.Title)}
                        >
                          {response?.Count}
                        </button>
                      </div>
                      <div>{response?.Percentage.toFixed(2)}%</div>
                      <ProgressBar
                        striped
                        variant="success"
                        now={response?.Percentage}
                        className="pg-block"
                      />
                    </div>
                  )
                )}
              </div>
            </Col>
            <Col>
              <div className="my-3">
                {pollStatistics?.PollStatisticAssestmentInfoList &&
                pollStatistics?.TotalRespondents > 0 ? (
                  <ESPieChart
                    data={pollStatistics?.PollStatisticAssestmentInfoList?.map(
                      (response: PollStatisticAssestmentInfo) => ({
                        Title: response?.Title,
                        Count: response?.Percentage,
                      })
                    )}
                    title="Poll Result"
                  />
                ) : (
                  <div className="alert alert-warning">
                    No data to show for Poll
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <div className="div-repondent d-flex justify-content-between text-start">
                <p>Total Respondents : {pollStatistics?.TotalRespondents}</p>
              </div>
              <div className="d-flex flex-row">
                <div className="alert alert-success p-2">
                  <img
                    src={liveico}
                    className="me-2"
                    alt=""
                    width="16"
                    height="14"
                  />
                  Live Result
                </div>
                <button
                  onClick={handleButtonClick}
                  className="btn btn-primary-outline"
                >
                  Back to Poll
                </button>
              </div>
            </Col>
          </Row>
          {showPopup && userPopUpData && (
            <ESPopUpForList
              onUpdateParentValue={handlepopupSHowHide}
              data={userPopUpData}
              header={popupHeader}
              model={userPopUpData}
              fileName={"UserData For Particular Option"}
              componentName={"UserDataForParticularOption"}
            />
          )}
        </div>
      )}
    </>
  );
};

export default PollResult;
